import React, { useEffect, useState } from 'react'

// Types and interfaces
type ValidationType = string

interface InputFieldOption {
    id: string
    type: ValidationType
    label: string
    enabled: boolean
    question: string
    errorMessage: string
    order: number
    isComingSoon?: boolean
}

interface OpeningQuestion {
    openingMessage: string
    endingMessage: string
}

interface UserInputProps {
    index: number;
    data: any;
    update_response_data: (data: any) => void;
}

const UserInput = ({ index, data, update_response_data }: UserInputProps) => {
    const [openingQuestion, setOpeningQuestion] = useState<OpeningQuestion>({
        openingMessage: data?.openingMessage || 'Before I send you the link, just wanted to know a few things!',
        endingMessage: data?.endingMessage || 'Thank you!✨ Here you go👇🏾'
    })

    const [inputFields, setInputFields] = useState<InputFieldOption[]>(data?.questions || [
        {
            id: '1',
            type: 'name',
            label: 'Name',
            enabled: false,
            question: 'Please enter your name',
            errorMessage: 'Please enter a valid name',
            order: 1
        },
        {
            id: '2',
            type: 'email',
            label: 'Email',
            enabled: false,
            question: 'Please enter your email address',
            errorMessage: 'Please enter a valid email address',
            order: 2
        },
        {
            id: '3',
            type: 'phone',
            label: 'Phone Number',
            enabled: false,
            question: 'Please enter your phone number with country code (eg: +918888888888)',
            errorMessage: 'Please enter a valid phone number',
            order: 3
        }
    ])

    // Toggle field enabled/disabled
    const toggleField = (id: string) => {
        if (inputFields.find(field => field.id === id)?.isComingSoon) return

        setInputFields(prev =>
            prev.map(field =>
                field.id === id ? { ...field, enabled: !field.enabled } : field
            )
        )
    }

    // Update field question
    const updateFieldQuestion = (id: string, question: string) => {
        setInputFields(prev =>
            prev.map(field =>
                field.id === id ? { ...field, question } : field
            )
        )
    }

    // Update field error message
    const updateFieldErrorMessage = (id: string, errorMessage: string) => {
        setInputFields(prev =>
            prev.map(field =>
                field.id === id ? { ...field, errorMessage } : field
            )
        )
    }

    // Update field label (for custom fields)
    const updateFieldLabel = (id: string, label: string) => {
        setInputFields(prev =>
            prev.map(field => {
                if (field.id === id) {
                    // Extract numeric ID from custom field id (e.g., "cus_4_old-label" -> "4")
                    const numericId = id.split('_')[1]
                    // Generate new id: cus_ + numericId + _ + label (lowercase, spaces replaced by -)
                    const newId = `cus_${numericId}_${label.trim().toLowerCase().replace(/\s+/g, '-')}`
                    return { ...field, label, id: newId }
                }
                return field
            })
        )
    }

    // Update input type (for custom fields)
    const updateInputType = (id: string, type: 'text' | 'number') => {
        setInputFields(prev =>
            prev.map(field =>
                field.id === id ? { ...field, type } : field
            )
        )
    }

    // Add custom field
    const addCustomField = () => {
        // Find the next available ID sequence
        const maxId = inputFields.reduce((max, field) => {
            const idNum = parseInt(field.id)
            return !isNaN(idNum) && idNum > max ? idNum : max
        }, 0)

        const newId = (maxId + 1).toString()
        const defaultLabel = 'Custom Question'

        const newField: InputFieldOption = {
            type: 'text',
            id: `cus_${newId}_${defaultLabel.toLowerCase().replace(/\s+/g, '-')}`,
            label: defaultLabel,
            enabled: true,
            question: 'Please enter the required information',
            errorMessage: 'This field is required',
            order: inputFields.length + 1,
        }

        setInputFields(prev => [...prev, newField])
    }

    // Delete field
    const deleteField = (id: string) => {
        setInputFields(prev => prev.filter(field => field.id !== id))
    }

    // Move field up in order
    const moveFieldUp = (id: string) => {
        const fieldIndex = inputFields.findIndex(field => field.id === id)
        if (fieldIndex <= 0) return

        const newFields = [...inputFields]
        // Create copies to avoid mutating read-only properties
        const currentField = { ...newFields[fieldIndex] }
        const prevField = { ...newFields[fieldIndex - 1] }

        const temp = currentField.order
        currentField.order = prevField.order
        prevField.order = temp

        newFields[fieldIndex] = currentField
        newFields[fieldIndex - 1] = prevField

        newFields.sort((a, b) => a.order - b.order)
        setInputFields(newFields)
    }

    // Move field down in order
    const moveFieldDown = (id: string) => {
        const fieldIndex = inputFields.findIndex(field => field.id === id)
        if (fieldIndex >= inputFields.length - 1) return

        const newFields = [...inputFields]
        // Create copies to avoid mutating read-only properties
        const currentField = { ...newFields[fieldIndex] }
        const nextField = { ...newFields[fieldIndex + 1] }

        const temp = currentField.order
        currentField.order = nextField.order
        nextField.order = temp

        newFields[fieldIndex] = currentField
        newFields[fieldIndex + 1] = nextField

        newFields.sort((a, b) => a.order - b.order)
        setInputFields(newFields)
    }

    const sortedFields = [...inputFields].sort((a, b) => a.order - b.order)



    useEffect(() => {
        update_response_data({
            ...data,
            type: 'user_input',
            openingMessage: openingQuestion.openingMessage,
            endingMessage: openingQuestion.endingMessage,
            questions: inputFields
        })
    }, [inputFields, openingQuestion])


    return (
        <div className='p-4 border bg-white border-gray-200 my-2 rounded-md space-y-4'>

            {/* Opening and Ending Messages */}
            <div className='space-y-3'>
                <div>
                    <label className='text-sm font-bold'>Opening Message</label>
                    <input
                        type='text'
                        placeholder='Enter the opening message'
                        value={openingQuestion.openingMessage}
                        onChange={(e) => setOpeningQuestion({ ...openingQuestion, openingMessage: e.target.value })}
                        className='p-2 border border-gray-300 mt-1 rounded-md w-full text-sm outline-none focus:border-blue-500'
                    />
                </div>

                <div>
                    <label className='text-sm font-bold'>Ending Message</label>
                    <input
                        type='text'
                        placeholder='Enter the ending message'
                        value={openingQuestion.endingMessage}
                        onChange={(e) => setOpeningQuestion({ ...openingQuestion, endingMessage: e.target.value })}
                        className='p-2 border border-gray-300 mt-1 rounded-md w-full text-sm outline-none focus:border-blue-500'
                    />
                </div>
            </div>

            {/* Input Field Options */}
            <div className='space-y-3'>
                <h3 className='text-md font-semibold'>Input Fields</h3>
                {sortedFields.map((field, index) => {
                    const isCustom = field.id.startsWith('cus_')
                    return (
                        <div key={field.order} className={`border rounded-md p-3 space-y-3 ${field.enabled ? 'border-blue-300 bg-blue-50' : 'border-gray-200'} ${field.isComingSoon ? 'opacity-60' : ''}`}>
                            {/* Field Header */}
                            <div className='flex items-center justify-between'>
                                <div className='flex items-center space-x-3 flex-1'>
                                    <label className='flex items-center space-x-2 cursor-pointer'>
                                        <input
                                            type='checkbox'
                                            checked={field.enabled}
                                            onChange={() => toggleField(field.id)}
                                            disabled={field.isComingSoon}
                                            className='w-4 h-4 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50'
                                        />
                                        {!isCustom ? (
                                            <span className='text-sm font-medium'>
                                                {field.label}
                                                {field.isComingSoon && <span className='ml-2 text-xs text-orange-600 font-normal'>(Coming Soon)</span>}
                                            </span>
                                        ) : (
                                            <div className="flex items-center">
                                                <input
                                                    type="text"
                                                    value={field.label}
                                                    onChange={(e) => updateFieldLabel(field.id, e.target.value)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className={`text-sm font-medium bg-transparent border-b focus:border-blue-500 focus:outline-none px-1 transition-colors ${field.label === 'Custom Question' ? 'border-red-400 text-red-500 placeholder-red-300' : 'border-transparent hover:border-blue-300'}`}
                                                />
                                                {field.label === 'Custom Question' && (
                                                    <span className="ml-2 text-[10px] text-red-500 font-medium animate-pulse">
                                                        Rename me!
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </label>
                                </div>

                                {/* Actions */}
                                <div className='flex items-center space-x-1'>
                                    {!field.isComingSoon && (
                                        <>
                                            <button
                                                onClick={() => moveFieldUp(field.id)}
                                                disabled={index === 0}
                                                className='p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed'
                                                title='Move up'
                                            >
                                                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 15l7-7 7 7' />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => moveFieldDown(field.id)}
                                                disabled={index === sortedFields.length - 1}
                                                className='p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed'
                                                title='Move down'
                                            >
                                                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                                                </svg>
                                            </button>
                                        </>
                                    )}
                                    {isCustom && (
                                        <button
                                            onClick={() => deleteField(field.id)}
                                            className='p-1 text-red-400 hover:text-red-600 ml-2'
                                            title='Delete field'
                                        >
                                            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Field Configuration (only show when enabled) */}
                            {field.enabled && !field.isComingSoon && (
                                <div className='space-y-3 pl-6'>
                                    {isCustom && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className='text-xs font-medium text-gray-600'>Input Type</label>
                                                <div className="flex items-center space-x-4 mt-1">
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name={`inputType-${field.id}`}
                                                            checked={field.type !== 'number'}
                                                            onChange={() => updateInputType(field.id, 'text')}
                                                            className="text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm text-gray-700">Text</span>
                                                    </label>
                                                    <label className="flex items-center space-x-2 cursor-pointer">
                                                        <input
                                                            type="radio"
                                                            name={`inputType-${field.id}`}
                                                            checked={field.type === 'number'}
                                                            onChange={() => updateInputType(field.id, 'number')}
                                                            className="text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm text-gray-700">Number</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className='text-xs font-medium text-gray-600'>Question Text</label>
                                        <input
                                            type='text'
                                            value={field.question}
                                            onChange={(e) => updateFieldQuestion(field.id, e.target.value)}
                                            className='p-2 border border-gray-300 mt-1 rounded-md w-full text-sm outline-none focus:border-blue-500'
                                            placeholder={`Enter question for ${field.label.toLowerCase()}`}
                                        />
                                    </div>
                                    <div>
                                        <label className='text-xs font-medium text-gray-600'>Error Message</label>
                                        <input
                                            type='text'
                                            value={field.errorMessage}
                                            onChange={(e) => updateFieldErrorMessage(field.id, e.target.value)}
                                            className='p-2 border border-gray-300 mt-1 rounded-md w-full text-sm outline-none focus:border-blue-500'
                                            placeholder={`Enter error message for ${field.label.toLowerCase()}`}
                                        />
                                    </div>
                                    {!isCustom && (
                                        <div className='text-xs text-gray-500'>
                                            Validation type: <span className='font-medium'>{field.type}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}

                <button
                    onClick={addCustomField}
                    className="w-full text-sm bg-blue-50 text-blue-600 px-3 py-2 rounded-md hover:bg-blue-100 transition-colors font-medium flex items-center justify-center gap-1 border border-blue-200 border-dashed"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Custom Question
                </button>
            </div>
        </div>
    )
}

export default UserInput